import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Sparkles, PhoneCall, MessageCircle, Truck, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import steamRoomImg from "@/assets/steam_room_hammam.webp";
import drinksImg from "@/assets/drinks_hammam.webp";
import newCommonShowerImg from "@/assets/new_common_shower.png";
import newDarkRoomImg from "@/assets/new_dark_room.png";
import newDrinksBarImg from "@/assets/new_driniks_bar.png";
import newHugeJacuzziImg from "@/assets/new_huge_jacuzzi.png";

export default function Doorstep() {
  const { user } = useAuth();
  const [activeDoorstep, setActiveDoorstep] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);

  useEffect(() => {
    if (user) {
      Promise.all([
        api.get("/bookings/mine"),
        api.get("/branches"),
      ]).then(([bRes, brRes]) => {
        const active = bRes.data?.find(
          (b) => b.booking_type === "doorstep" && b.status !== "completed" && b.status !== "cancelled"
        );
        if (active) {
          setActiveDoorstep(active);
          const br = brRes.data?.find((x) => x.id === active.branch_id) || brRes.data?.[0];
          setBranchInfo(br);
        }
      }).catch(() => {});
    }
  }, [user]);

  return (
    <div data-testid="doorstep-page">
      {/* Active Doorstep Request Banner */}
      {activeDoorstep && (
        <div className="bg-gradient-to-r from-emerald-950 via-black to-amber-950 border-b-2 border-emerald-400/80 px-6 py-4">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-emerald-400">
                <Truck size={16} /> ACTIVE DOORSTEP REQUEST ({activeDoorstep.id?.slice(0, 8)})
              </div>
              <p className="text-sm font-bold text-white">
                Our team at <span className="text-amber-300">{branchInfo?.name || "Hammam Branch"}</span> will connect with you shortly! You can also call us directly right now.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a
                href={`tel:${branchInfo?.phone || "+919560656913"}`}
                className="gold-btn py-2 px-4 rounded-xl text-xs font-extrabold uppercase tracking-wider flex items-center gap-1.5 shadow-md"
              >
                <PhoneCall size={14} /> Call Branch ({branchInfo?.phone || "+91 9560656913"})
              </a>
              <a
                href={`https://wa.me/919560656913?text=Hi%20Hammam%20Spa,%20I%20have%20an%20active%20doorstep%20booking%20request%20(${activeDoorstep.id?.slice(0,8)}).`}
                target="_blank"
                rel="noreferrer"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold py-2 px-4 rounded-xl text-xs uppercase tracking-wider flex items-center gap-1.5 shadow-md transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[70vh] overflow-hidden -mt-[72px] flex items-center">
        <img src="/hero_image_hammam.png" alt="Doorstep" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
          className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-20 w-full"
        >
          <div className="label-eyebrow mb-6 text-emerald-400 font-extrabold tracking-widest uppercase">Best Gay Spa in New Delhi</div>
          <h1 className="text-5xl lg:text-7xl font-heading text-white max-w-3xl leading-tight">
            Hammam,<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-yellow-400 italic">at your doorstep.</span>
          </h1>
          <p className="text-hsecondary text-lg max-w-2xl mt-8 leading-relaxed font-normal">
            Our therapists arrive with a professional massage table, oils, ambient sound and warm towels. We recreate the sanctuary in your living room, hotel suite, or private residence.
          </p>
          <Link to="/book?type=doorstep" className="gold-btn mt-10 rounded-xl" data-testid="doorstep-book-cta">
            Reserve doorstep session <ArrowRight size={16}/>
          </Link>
        </motion.div>
      </section>

      {/* Feature Cards Section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          {
            icon: MapPin,
            t: "Delhi + NCR",
            d: "We deliver across all of Delhi, Gurugram, Noida and Faridabad.",
            cardBorder: "border-2 border-red-500/80 hover:border-red-400 shadow-xl shadow-red-950/30",
            iconCircle: "border border-red-500/60 bg-red-950/20 text-red-400",
            titleColor: "text-red-400",
          },
          {
            icon: Clock,
            t: "Same-day slots",
            d: "Book by 4 PM for an evening session; earlier for afternoon.",
            cardBorder: "border-2 border-amber-400/80 hover:border-amber-300 shadow-xl shadow-amber-950/30",
            iconCircle: "border border-amber-400/60 bg-amber-950/20 text-amber-400",
            titleColor: "text-amber-400",
          },
          {
            icon: Sparkles,
            t: "Same rituals",
            d: "All packages and services available at your doorstep.",
            cardBorder: "border-2 border-emerald-400/80 hover:border-emerald-300 shadow-xl shadow-emerald-950/30",
            iconCircle: "border border-emerald-400/60 bg-emerald-950/20 text-emerald-400",
            titleColor: "text-emerald-400",
          },
        ].map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.15, ease: [0.25, 1, 0.5, 1] }}
            className={`p-8 rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper ${f.cardBorder} transition-all duration-300 group`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110 ${f.iconCircle}`}>
              <f.icon size={22} />
            </div>
            <h3 className={`font-heading text-2xl mb-3 font-bold ${f.titleColor}`}>{f.t}</h3>
            <p className="text-hsecondary text-sm leading-relaxed font-normal">{f.d}</p>
          </motion.div>
        ))}
      </section>

      {/* Divider Line */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-hgold/40 to-transparent my-4 max-w-7xl mx-auto" />

      {/* Facilities Grid Section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 mb-3">
            Our Elite. Discreet. Relaxing. Facilities
          </h2>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400/90 font-bold">For The Modern Gentleman</div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { img: steamRoomImg, title: "STEAM & SAUNA", border: "border-2 border-red-500 shadow-lg shadow-red-950/40" },
            { img: drinksImg, title: "SUNBATH AREA", border: "border-2 border-emerald-400 shadow-lg shadow-emerald-950/40" },
            { img: newHugeJacuzziImg, title: "HUGE JACUZZI", border: "border-2 border-amber-400 shadow-lg shadow-amber-950/40" },
            { img: newCommonShowerImg, title: "COMMON SHOWER", border: "border-2 border-rose-400 shadow-lg shadow-rose-950/40" },
            { img: newDarkRoomImg, title: "DISCREET DARK ROOM", border: "border-2 border-hgold shadow-lg shadow-yellow-950/40" },
            { img: newDrinksBarImg, title: "DRINKS & LOUNGE BAR", border: "border-2 border-cyan-400 shadow-lg shadow-cyan-950/40" }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.25, 1, 0.5, 1] }}
              className={`relative rounded-2xl overflow-hidden ${item.border} group`}
            >
              <div className="aspect-square overflow-hidden bg-black/40">
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
